import React, { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import { supabase } from '../supabaseClient';

/**
 * List Display Component
 * Shows a list with checkboxes that can be ticked
 * Updates in real-time across all users
 */
const ListDisplay = ({ list, currentUserId }) => {
  const [checkedItems, setCheckedItems] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);

  // Parse list data from message_text if it's a string
  const listData = React.useMemo(() => {
    try {
      if (typeof list.message_text === 'string') {
        const parsed = JSON.parse(list.message_text);
        return {
          list_title: parsed.list_title || '',
          list_items: parsed.list_items || []
        };
      }
      return {
        list_title: list.list_title || '',
        list_items: list.list_items || []
      };
    } catch (error) {
      console.error('Error parsing list data:', error);
      return {
        list_title: '',
        list_items: []
      };
    }
  }, [list.message_text, list.list_title, list.list_items]);

  useEffect(() => {
    // Initialize checked state from list data only once when list.id changes
    if (listData.list_items) {
      const initialChecked = {};
      listData.list_items.forEach((item, index) => {
        initialChecked[index] = item.checked || false;
      });
      setCheckedItems(initialChecked);
    }
  }, [list.id]); // Only depend on list.id to prevent infinite loop

  useEffect(() => {
    // Subscribe to real-time updates for this list
    const subscription = supabase
      .channel(`list:${list.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'team_chat',
          filter: `id=eq.${list.id}`
        },
        (payload) => {
          console.log('[List] Real-time update:', payload);
          // Parse message_text to get updated list data
          if (payload.new.message_text) {
            try {
              const parsed = JSON.parse(payload.new.message_text);
              if (parsed.list_items) {
                const updatedChecked = {};
                parsed.list_items.forEach((item, index) => {
                  updatedChecked[index] = item.checked || false;
                });
                setCheckedItems(updatedChecked);
              }
            } catch (error) {
              console.error('Error parsing updated list data:', error);
            }
          }
        }
      )
      .subscribe((status) => {
        console.log('[List] Subscription status:', status);
      });

    return () => {
      supabase.removeChannel(subscription);
    };
  }, [list.id]);

  const handleToggleItem = async (index) => {
    if (isUpdating) return;

    setIsUpdating(true);

    // Optimistically update local state
    const newCheckedItems = {
      ...checkedItems,
      [index]: !checkedItems[index]
    };
    setCheckedItems(newCheckedItems);

    try {
      // Update list items with new checked state
      const updatedItems = listData.list_items.map((item, i) => ({
        ...item,
        checked: i === index ? !checkedItems[index] : (checkedItems[i] || false)
      }));

      const updatedListData = {
        __type: 'list',
        list_title: listData.list_title,
        list_items: updatedItems
      };

      const { error } = await supabase
        .from('team_chat')
        .update({
          message_text: JSON.stringify(updatedListData),
          updated_at: new Date().toISOString()
        })
        .eq('id', list.id);

      if (error) throw error;

      console.log('[List] Item toggled successfully');
    } catch (error) {
      console.error('Error toggling list item:', error);
      // Revert optimistic update on error
      setCheckedItems(checkedItems);
    } finally {
      setIsUpdating(false);
    }
  };

  // Calculate progress
  const totalItems = listData.list_items?.length || 0;
  const checkedCount = Object.values(checkedItems).filter(Boolean).length;
  const progressPercent = totalItems > 0 ? (checkedCount / totalItems) * 100 : 0;

  return (
    <div className="w-full bg-[#0d1117] border border-white/20 rounded-lg overflow-hidden my-3">
      {/* Table Header with Title and Progress */}
      <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 border-b border-white/20 px-4 py-3">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-white flex items-center gap-2">
            <span>📋</span>
            {listData.list_title || 'Checklist'}
          </h3>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400">
              {checkedCount} / {totalItems} completed
            </span>
            <div className="w-24 h-2 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Table Body */}
      <table className="w-full">
        <thead>
          <tr className="border-b border-white/10 bg-white/5">
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-12">
              Status
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
              Task
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
              Details
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {listData.list_items?.map((item, index) => (
            <tr
              key={index}
              className={`transition-all hover:bg-white/5 ${
                checkedItems[index] ? 'opacity-60' : ''
              }`}
            >
              {/* Checkbox Column */}
              <td className="px-4 py-3">
                <button
                  onClick={() => handleToggleItem(index)}
                  disabled={isUpdating}
                  className={`flex items-center justify-center w-5 h-5 rounded border-2 transition-all ${
                    checkedItems[index]
                      ? 'bg-blue-600 border-blue-600'
                      : 'border-white/30 hover:border-blue-500'
                  } ${isUpdating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  {checkedItems[index] && (
                    <Check className="w-3 h-3 text-white" strokeWidth={3} />
                  )}
                </button>
              </td>

              {/* Task Text Column */}
              <td className="px-4 py-3">
                <div
                  className={`text-sm font-medium transition-all ${
                    checkedItems[index]
                      ? 'line-through text-gray-500'
                      : 'text-white'
                  }`}
                >
                  {item.text}
                </div>
              </td>

              {/* Details/Subtitle Column */}
              <td className="px-4 py-3">
                {item.subtitle && (
                  <div
                    className={`text-xs transition-all ${
                      checkedItems[index]
                        ? 'line-through text-gray-600'
                        : 'text-gray-400'
                    }`}
                  >
                    {item.subtitle}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ListDisplay;
